import { useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { ChevronLeft, Search, CheckCircle, Clock, User, AlertCircle } from 'lucide-react';
import { usePageTitle, Avatar } from '../components/AppShell';
import { apiGet, apiPost, type AuthUser } from '../lib/auth';
import { Toast, type ToastVariant } from '../components/Toast';
import { useLang } from '../lib/lang';
import { T } from '../lib/tokens';

interface Props { user: AuthUser }

interface Teacher {
  id: string; full_name_en: string | null; full_name_ms: string | null;
  preferred_name: string | null; designation: string | null;
  email: string | null; isAvailable: boolean;
}
interface Period {
  id: string; day_of_week: number; period_number: number;
  start_time: string; end_time: string; period_type: string;
}

const DAYS_MS = ['Isnin','Selasa','Rabu','Khamis','Jumaat','Sabtu'];
const DAYS_EN = ['Monday','Tuesday','Wednesday','Thursday','Friday','Saturday'];

const DEMO_SCHOOL_ID  = '3f868b12-b554-4f51-a893-69e9b4ba9f57';
const DEMO_VERSION_ID = '00000000-0000-0000-0000-000000000001';

const C = T.color;

export function SubstitutePage({ user }: Props) {
  const { lang } = useLang();
  const E = lang === 'en';
  const navigate = useNavigate();
  usePageTitle(E ? 'Substitution' : 'Penggantian');

  const DAYS = E ? DAYS_EN : DAYS_MS;
  const schoolId = user.roles.find((r) => r.schoolId)?.schoolId ?? DEMO_SCHOOL_ID;

  const today = new Date().toISOString().slice(0, 10);

  const [date,            setDate]            = useState(today);
  const [dayOfWeek,       setDayOfWeek]       = useState('1');
  const [selectedPeriod,  setSelectedPeriod]  = useState('');
  const [selectedSlot,    setSelectedSlot]    = useState('');
  const [reason,          setReason]          = useState('');

  const [periods,         setPeriods]         = useState<Period[]>([]);
  const [teachers,        setTeachers]        = useState<Teacher[]>([]);
  const [selectedTeacher, setSelectedTeacher] = useState('');

  const [periodsLoading,  setPeriodsLoading]  = useState(false);
  const [teachersLoading, setTeachersLoading] = useState(false);
  const [assigning,       setAssigning]       = useState(false);

  const [periodsError,    setPeriodsError]    = useState<string | null>(null);
  const [teachersError,   setTeachersError]   = useState<string | null>(null);
  const [formError,       setFormError]       = useState<string | null>(null);
  const [toast,           setToast]           = useState<{ message: string; variant: ToastVariant } | null>(null);

  const isAdmin = user.roles.some((r) => ['admin', 'principal', 'super_admin'].includes(r.role));

  // ── Load periods for selected day ───────────────────────────────────────────
  const loadPeriods = useCallback(async () => {
    setPeriodsLoading(true); setPeriodsError(null);
    try {
      const data = await apiGet<(Period & { is_active: boolean })[]>(`/api/timetable/periods/${schoolId}`);
      setPeriods(data.filter((p) => p.day_of_week === parseInt(dayOfWeek) && p.is_active));
    } catch (err) {
      setPeriodsError(err instanceof Error ? err.message : (E ? 'Error loading periods.' : 'Ralat memuatkan waktu.'));
    } finally { setPeriodsLoading(false); }
  }, [schoolId, dayOfWeek, E]);

  // ── Load available teachers for the period ──────────────────────────────────
  const loadTeachers = useCallback(async (periodId: string) => {
    if (!periodId) return;
    setTeachersLoading(true); setTeachersError(null); setTeachers([]);
    try {
      const data = await apiGet<Teacher[]>(
        `/api/timetable/substitute/available?schoolId=${schoolId}&date=${date}&dayOfWeek=${dayOfWeek}&periodId=${periodId}&versionId=${DEMO_VERSION_ID}`,
      );
      setTeachers(data);
    } catch (err) {
      setTeachersError(err instanceof Error ? err.message : (E ? 'Error loading teachers.' : 'Ralat memuatkan senarai guru.'));
    } finally { setTeachersLoading(false); }
  }, [schoolId, date, dayOfWeek, E]);

  function handleDateChange(val: string) {
    setDate(val);
    setPeriods([]); setTeachers([]); setSelectedPeriod(''); setSelectedTeacher('');
    setFormError(null);
  }

  function handleDayChange(val: string) {
    setDayOfWeek(val);
    setPeriods([]); setTeachers([]); setSelectedPeriod(''); setSelectedTeacher('');
  }

  function validateAndSearch() {
    if (!date) { setFormError(E ? 'Please select a date.' : 'Sila pilih tarikh.'); return; }
    if (date < today) { setFormError(E ? 'Date cannot be in the past.' : 'Tarikh tidak boleh dalam masa lalu.'); return; }
    setFormError(null);
    setSelectedPeriod(''); setSelectedTeacher(''); setTeachers([]);
    void loadPeriods();
  }

  function handleSelectPeriod(periodId: string) {
    setSelectedPeriod(periodId);
    setSelectedTeacher('');
    void loadTeachers(periodId);
  }

  async function handleAssign() {
    if (!selectedSlot.trim()) { setFormError(E ? 'Please enter the original slot ID.' : 'Sila masukkan ID slot asal.'); return; }
    if (!selectedTeacher)     { setFormError(E ? 'Please select a substitute teacher.' : 'Sila pilih guru pengganti.'); return; }
    if (!date)                { setFormError(E ? 'Date is required.' : 'Tarikh diperlukan.'); return; }
    setAssigning(true); setFormError(null);
    try {
      await apiPost('/api/timetable/substitute', {
        originalSlotId: selectedSlot.trim(), date,
        substituteTeacherId: selectedTeacher,
        reason: reason.trim() || undefined,
      });
      setToast({ variant: 'success', message: E ? 'Substitute teacher assigned successfully.' : 'Guru pengganti berjaya ditugaskan.' });
      setSelectedSlot(''); setSelectedTeacher(''); setReason('');
      void loadTeachers(selectedPeriod);
    } catch (e) {
      setFormError(e instanceof Error ? e.message : (E ? 'Failed to assign substitute.' : 'Gagal menugaskan pengganti.'));
    } finally { setAssigning(false); }
  }

  const selectedTeacherObj = teachers.find((t) => t.id === selectedTeacher);

  // ── Step indicator ───────────────────────────────────────────────────────────
  const steps = [
    { num: 1, done: !!date,                         label: E ? 'Date & Day'  : 'Tarikh & Hari'  },
    { num: 2, done: !!selectedPeriod,               label: E ? 'Period'      : 'Waktu'          },
    { num: 3, done: !!selectedTeacher,              label: E ? 'Teacher'     : 'Guru'           },
    { num: 4, done: false,                          label: E ? 'Assign'      : 'Tugaskan'       },
  ];

  return (
    <>
      {/* ── Scoped date-input fix: make picker indicator visible in all themes ── */}
      <style>{`
        .sub-date-input::-webkit-calendar-picker-indicator {
          cursor: pointer;
          opacity: 0.7;
          filter: invert(var(--picker-invert, 0));
          padding: 2px;
        }
        .sub-date-input::-webkit-calendar-picker-indicator:hover { opacity: 1; }
        html[data-theme="dark"] .sub-date-input::-webkit-calendar-picker-indicator {
          filter: invert(1);
        }
        .sub-date-input { cursor: text; }
        .sub-input {
          width: 100%; background: var(--color-input-bg);
          border: 1.5px solid var(--color-border-strong);
          border-radius: 8px; color: var(--color-text-primary);
          padding: 10px 14px; font-size: 14px;
          font-family: var(--font-sans); outline: none;
          transition: border-color 120ms, box-shadow 120ms;
          cursor: text;
        }
        .sub-input::placeholder { color: var(--color-text-placeholder); }
        .sub-input:hover:not(:disabled) { border-color: var(--color-border-strong); }
        .sub-input:focus {
          border-color: var(--royal-yellow);
          box-shadow: 0 0 0 3px rgba(247,208,0,0.14);
        }
        .sub-input:disabled { opacity: 0.5; cursor: not-allowed; }
        .sub-select { cursor: pointer; }
      `}</style>

      <div style={{ maxWidth: '760px' }}>

        {/* Back + header */}
        <div className="page-header">
          <div>
            <button
              onClick={() => navigate('/admin/timetable')}
              style={{ display: 'flex', alignItems: 'center', gap: '4px', background: 'none', border: 'none', cursor: 'pointer', color: C.textSecondary, fontSize: '13px', padding: 0, marginBottom: '10px', fontFamily: T.font.sans }}
            >
              <ChevronLeft size={14} /> {E ? 'Back to Timetable' : 'Kembali ke Jadual'}
            </button>
            <h1 style={{ fontFamily: T.font.serif, fontSize: '22px', fontWeight: 700, color: C.textPrimary, marginBottom: '4px' }}>
              {E ? 'Substitute Teacher Assignment' : 'Penggantian Guru'}
            </h1>
            <p style={{ fontSize: '13px', color: C.textSecondary }}>
              {E ? 'Find an available teacher to cover a period' : 'Cari guru yang tersedia untuk menggantikan satu waktu'}
            </p>
          </div>
        </div>

        {/* Step progress */}
        <div style={{ display: 'flex', gap: '8px', marginBottom: '24px', flexWrap: 'wrap' }}>
          {steps.map((s, i) => (
            <div key={s.num} style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
              <div style={{
                width: '24px', height: '24px', borderRadius: '50%', flexShrink: 0,
                background: s.done ? '#0D7A4E' : s.num === (selectedPeriod ? (selectedTeacher ? 4 : 3) : (periods.length ? 2 : 1)) ? '#F7D000' : C.cardAlt,
                border: `2px solid ${s.done ? '#0D7A4E' : s.num === (selectedPeriod ? (selectedTeacher ? 4 : 3) : (periods.length ? 2 : 1)) ? '#F7D000' : C.borderStrong}`,
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                fontSize: '10px', fontWeight: 700,
                color: s.done ? '#FFFFFF' : s.num === 1 ? '#111827' : C.textSecondary,
                transition: 'all 200ms ease',
              }}>
                {s.done ? '✓' : s.num}
              </div>
              <span style={{ fontSize: '12px', color: s.done ? '#0D7A4E' : C.textSecondary, fontWeight: s.done ? 600 : 400 }}>
                {s.label}
              </span>
              {i < steps.length - 1 && (
                <div style={{ width: '20px', height: '1px', background: C.border, margin: '0 2px' }} />
              )}
            </div>
          ))}
        </div>

        {/* ── STEP 1: Date & Day ─────────────────────────────────────────────── */}
        <div style={{ background: C.card, border: `1px solid ${C.border}`, borderRadius: '10px', padding: '20px 24px', marginBottom: '16px', boxShadow: T.shadow.sm }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '16px' }}>
            <div style={{ width: '6px', height: '20px', background: '#F7D000', borderRadius: '3px' }} />
            <h2 style={{ fontFamily: T.font.serif, fontSize: '14px', fontWeight: 700, color: C.textPrimary }}>
              {E ? 'Step 1 — Select Date & Day' : 'Langkah 1 — Pilih Tarikh & Hari'}
            </h2>
          </div>

          <div style={{ display: 'flex', gap: '16px', flexWrap: 'wrap', marginBottom: '16px' }}>
            {/* DATE — the field that was broken */}
            <div style={{ flex: '1 1 180px' }}>
              <label style={{ display: 'block', fontSize: '12px', fontWeight: 600, color: C.textSecondary, marginBottom: '6px' }}>
                {E ? 'Date' : 'Tarikh'} <span style={{ color: '#B91C1C' }}>*</span>
              </label>
              <input
                type="date"
                className="sub-input sub-date-input"
                value={date}
                min={today}
                onChange={(e) => handleDateChange(e.target.value)}
                style={{ appearance: 'auto', WebkitAppearance: 'auto' } as React.CSSProperties}
              />
            </div>

            {/* DAY dropdown */}
            <div style={{ flex: '1 1 180px' }}>
              <label style={{ display: 'block', fontSize: '12px', fontWeight: 600, color: C.textSecondary, marginBottom: '6px' }}>
                {E ? 'Day of Week' : 'Hari'} <span style={{ color: '#B91C1C' }}>*</span>
              </label>
              <select
                className="sub-input sub-select"
                value={dayOfWeek}
                onChange={(e) => handleDayChange(e.target.value)}
              >
                {DAYS.map((d, i) => (
                  <option key={i + 1} value={i + 1}>{d}</option>
                ))}
              </select>
            </div>
          </div>

          {formError && (
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', background: 'var(--color-danger-light)', border: '1px solid #FECACA', borderRadius: '8px', padding: '10px 14px', marginBottom: '14px' }}>
              <AlertCircle size={14} color="#B91C1C" />
              <span style={{ fontSize: '13px', color: '#991B1B' }}>{formError}</span>
            </div>
          )}

          <button
            className="btn-primary"
            onClick={validateAndSearch}
            disabled={periodsLoading || !date}
            style={{ height: '40px', minWidth: '140px' }}
          >
            {periodsLoading ? (
              <><span style={{ width: '14px', height: '14px', border: '2px solid rgba(0,0,0,0.2)', borderTopColor: '#111827', borderRadius: '50%', animation: 'spin 0.7s linear infinite', display: 'inline-block' }} /> {E ? 'Loading…' : 'Memuatkan…'}</>
            ) : (
              <><Search size={14} /> {E ? 'Find Periods' : 'Cari Waktu'}</>
            )}
          </button>
        </div>

        {/* ── STEP 2: Period selection ──────────────────────────────────────── */}
        {periodsError && (
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', background: 'var(--color-danger-light)', border: '1px solid #FECACA', borderRadius: '8px', padding: '12px 16px', marginBottom: '16px' }}>
            <AlertCircle size={14} color="#B91C1C" />
            <span style={{ fontSize: '13px', color: '#991B1B' }}>{periodsError}</span>
          </div>
        )}

        {periods.length > 0 && (
          <div style={{ background: C.card, border: `1px solid ${C.border}`, borderRadius: '10px', padding: '20px 24px', marginBottom: '16px', boxShadow: T.shadow.sm }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '16px' }}>
              <div style={{ width: '6px', height: '20px', background: selectedPeriod ? '#0D7A4E' : '#F7D000', borderRadius: '3px' }} />
              <h2 style={{ fontFamily: T.font.serif, fontSize: '14px', fontWeight: 700, color: C.textPrimary }}>
                {E ? 'Step 2 — Select Period' : 'Langkah 2 — Pilih Waktu'}
              </h2>
            </div>
            <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
              {periods.map((p) => {
                const sel = selectedPeriod === p.id;
                return (
                  <button
                    key={p.id}
                    onClick={() => handleSelectPeriod(p.id)}
                    disabled={teachersLoading || assigning}
                    style={{
                      padding: '10px 16px', minWidth: '90px',
                      background: sel ? '#F7D000' : C.cardAlt,
                      border: `1.5px solid ${sel ? '#F7D000' : C.border}`,
                      borderRadius: '8px', cursor: (teachersLoading || assigning) ? 'not-allowed' : 'pointer',
                      opacity: (teachersLoading || assigning) ? 0.6 : 1,
                      fontFamily: T.font.sans, transition: 'all 120ms ease',
                      textAlign: 'center' as const,
                      boxShadow: sel ? '0 2px 8px rgba(247,208,0,0.25)' : 'none',
                    }}
                    onMouseEnter={e => { if (!sel) (e.currentTarget as HTMLElement).style.borderColor = '#F7D000'; }}
                    onMouseLeave={e => { if (!sel) (e.currentTarget as HTMLElement).style.borderColor = C.border; }}
                  >
                    <div style={{ fontSize: '14px', fontWeight: 700, color: sel ? '#111827' : C.textPrimary }}>
                      P{p.period_number}
                    </div>
                    <div style={{ fontSize: '11px', color: sel ? '#374151' : C.textSecondary, marginTop: '2px' }}>
                      {p.start_time}–{p.end_time}
                    </div>
                  </button>
                );
              })}
            </div>
          </div>
        )}

        {/* ── STEP 3: Available teachers ────────────────────────────────────── */}
        {teachersError && (
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', background: 'var(--color-danger-light)', border: '1px solid #FECACA', borderRadius: '8px', padding: '12px 16px', marginBottom: '16px' }}>
            <AlertCircle size={14} color="#B91C1C" />
            <span style={{ fontSize: '13px', color: '#991B1B' }}>{teachersError}</span>
          </div>
        )}

        {teachersLoading && (
          <div style={{ background: C.card, border: `1px solid ${C.border}`, borderRadius: '10px', padding: '32px', textAlign: 'center', marginBottom: '16px' }}>
            <div style={{ display: 'inline-flex', alignItems: 'center', gap: '10px', color: C.textSecondary, fontSize: '13px' }}>
              <span style={{ width: '18px', height: '18px', border: '2.5px solid rgba(0,0,0,0.1)', borderTopColor: '#F7D000', borderRadius: '50%', animation: 'spin 0.7s linear infinite', display: 'inline-block' }} />
              {E ? 'Finding available teachers…' : 'Mencari guru yang tersedia…'}
            </div>
          </div>
        )}

        {!teachersLoading && selectedPeriod && teachers.length === 0 && !teachersError && (
          <div style={{ background: C.card, border: `1px solid ${C.border}`, borderRadius: '10px', padding: '28px', textAlign: 'center', marginBottom: '16px', color: C.textSecondary, fontSize: '13px' }}>
            {E ? 'No active teachers found for this school.' : 'Tiada guru aktif dijumpai untuk sekolah ini.'}
          </div>
        )}

        {teachers.length > 0 && (
          <div style={{ background: C.card, border: `1px solid ${C.border}`, borderRadius: '10px', padding: '20px 24px', marginBottom: '16px', boxShadow: T.shadow.sm }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '16px' }}>
              <div style={{ width: '6px', height: '20px', background: selectedTeacher ? '#0D7A4E' : '#F7D000', borderRadius: '3px' }} />
              <h2 style={{ fontFamily: T.font.serif, fontSize: '14px', fontWeight: 700, color: C.textPrimary }}>
                {E ? 'Step 3 — Select Substitute Teacher' : 'Langkah 3 — Pilih Guru Pengganti'}
              </h2>
              <span style={{ marginLeft: 'auto', fontSize: '12px', color: C.textSecondary }}>
                {teachers.filter(t => t.isAvailable).length} {E ? 'available' : 'tersedia'} / {teachers.length} {E ? 'total' : 'jumlah'}
              </span>
            </div>

            <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', maxHeight: '360px', overflowY: 'auto' }}>
              {teachers.map((t) => {
                const name = t.preferred_name ?? t.full_name_ms ?? t.full_name_en ?? '—';
                const sel  = selectedTeacher === t.id;
                return (
                  <div
                    key={t.id}
                    onClick={() => t.isAvailable && setSelectedTeacher(t.id)}
                    style={{
                      display: 'flex', alignItems: 'center', gap: '14px',
                      padding: '13px 16px',
                      background: sel ? 'rgba(247,208,0,0.08)' : C.cardAlt,
                      border: `1.5px solid ${sel ? '#F7D000' : t.isAvailable ? C.border : C.borderSubtle}`,
                      borderRadius: '8px',
                      cursor: t.isAvailable ? 'pointer' : 'not-allowed',
                      opacity: t.isAvailable ? 1 : 0.4,
                      transition: 'all 120ms ease',
                    }}
                    onMouseEnter={e => { if (t.isAvailable && !sel) (e.currentTarget as HTMLElement).style.borderColor = '#F7D000'; }}
                    onMouseLeave={e => { if (!sel) (e.currentTarget as HTMLElement).style.borderColor = t.isAvailable ? C.border : C.borderSubtle; }}
                  >
                    <Avatar email={t.email ?? name} size={36} />
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontSize: '14px', fontWeight: sel ? 700 : 500, color: sel ? '#F7D000' : C.textPrimary, display: 'flex', alignItems: 'center', gap: '6px' }}>
                        {name}
                        {sel && <CheckCircle size={14} color="#0D7A4E" />}
                      </div>
                      <div style={{ fontSize: '12px', color: C.textSecondary, marginTop: '2px' }}>
                        {t.designation ?? ''}
                        {t.email ? ` · ${t.email}` : ''}
                      </div>
                    </div>
                    <span style={{
                      fontSize: '11px', fontWeight: 700, flexShrink: 0,
                      padding: '3px 10px', borderRadius: '999px',
                      background: t.isAvailable ? 'var(--color-success-light)' : 'var(--color-danger-light)',
                      color: t.isAvailable ? '#0D7A4E' : '#B91C1C',
                    }}>
                      {t.isAvailable ? (E ? 'Available' : 'Tersedia') : (E ? 'Busy' : 'Sibuk')}
                    </span>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* ── STEP 4: Confirm assignment ────────────────────────────────────── */}
        {isAdmin && selectedTeacher && (
          <div style={{ background: C.card, border: `1.5px solid #F7D000`, borderRadius: '10px', padding: '20px 24px', marginBottom: '16px', boxShadow: '0 4px 12px rgba(247,208,0,0.12)' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '16px' }}>
              <div style={{ width: '6px', height: '20px', background: '#F7D000', borderRadius: '3px' }} />
              <h2 style={{ fontFamily: T.font.serif, fontSize: '14px', fontWeight: 700, color: C.textPrimary }}>
                {E ? 'Step 4 — Confirm Assignment' : 'Langkah 4 — Sahkan Penugasan'}
              </h2>
            </div>

            {/* Summary strip */}
            <div style={{ background: 'rgba(247,208,0,0.08)', border: '1px solid rgba(247,208,0,0.2)', borderRadius: '8px', padding: '12px 16px', marginBottom: '16px', display: 'flex', alignItems: 'center', gap: '12px', flexWrap: 'wrap' }}>
              <User size={15} color="#F7D000" />
              <div>
                <div style={{ fontSize: '13px', fontWeight: 700, color: '#F7D000' }}>
                  {selectedTeacherObj?.preferred_name ?? selectedTeacherObj?.full_name_en ?? '—'}
                </div>
                <div style={{ fontSize: '11px', color: C.textSecondary }}>
                  {DAYS[parseInt(dayOfWeek) - 1]} · {date} · {E ? 'Period' : 'Waktu'} {periods.find(p => p.id === selectedPeriod)?.period_number ?? '—'}
                </div>
              </div>
            </div>

            <div style={{ display: 'flex', gap: '16px', flexWrap: 'wrap', marginBottom: '16px' }}>
              <div style={{ flex: '1 1 200px' }}>
                <label style={{ display: 'block', fontSize: '12px', fontWeight: 600, color: C.textSecondary, marginBottom: '6px' }}>
                  {E ? 'Original Slot ID' : 'ID Slot Asal'} <span style={{ color: '#B91C1C' }}>*</span>
                </label>
                <input
                  type="text"
                  className="sub-input"
                  value={selectedSlot}
                  onChange={(e) => setSelectedSlot(e.target.value)}
                  placeholder={E ? 'Timetable slot UUID' : 'UUID slot jadual'}
                  disabled={assigning}
                />
              </div>
              <div style={{ flex: '1 1 200px' }}>
                <label style={{ display: 'block', fontSize: '12px', fontWeight: 600, color: C.textSecondary, marginBottom: '6px' }}>
                  {E ? 'Reason (optional)' : 'Sebab (pilihan)'}
                </label>
                <input
                  type="text"
                  className="sub-input"
                  value={reason}
                  onChange={(e) => setReason(e.target.value)}
                  placeholder={E ? 'e.g. Teacher sick' : 'cth: Guru sakit'}
                  maxLength={500}
                  disabled={assigning}
                />
              </div>
            </div>

            {formError && (
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px', background: 'var(--color-danger-light)', border: '1px solid #FECACA', borderRadius: '8px', padding: '10px 14px', marginBottom: '14px' }}>
                <AlertCircle size={14} color="#B91C1C" />
                <span style={{ fontSize: '13px', color: '#991B1B' }}>{formError}</span>
              </div>
            )}

            <button
              className="btn-primary"
              onClick={() => void handleAssign()}
              disabled={assigning || !selectedSlot.trim()}
              style={{ height: '44px', minWidth: '200px', fontSize: '14px' }}
            >
              {assigning ? (
                <><span style={{ width: '14px', height: '14px', border: '2px solid rgba(0,0,0,0.2)', borderTopColor: '#111827', borderRadius: '50%', animation: 'spin 0.7s linear infinite', display: 'inline-block' }} /> {E ? 'Assigning…' : 'Menugaskan…'}</>
              ) : (
                <><CheckCircle size={16} /> {E ? 'Assign Substitute' : 'Tugaskan Pengganti'}</>
              )}
            </button>
          </div>
        )}
      </div>

      {toast && <Toast message={toast.message} variant={toast.variant} onDismiss={() => setToast(null)} />}
    </>
  );
}
